import { defineMessages, type IntlShape } from 'react-intl'

import { addHours, isWithinInterval } from 'date-fns'

import { AssessmentStages } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { type Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'

import { parseOutcomeResponseDate } from './assessmentHistory/outcomeDate'
import { type FlexibleAssessment, ONE_OFF_FREQUENCY } from './data/flexibleAssessmentsApi'

export type AssessmentFrequency = number

export type AssessmentOption = {
  label: string
  value: string
}

export type FrequencyOption = {
  label: string
  value: AssessmentFrequency
}

/** `edit` renders the banner message as a link into the edit flow. */
export type AssessmentModalBannerAction = 'edit'

export type AssessmentModalBanner = {
  type: 'info' | 'warning'
  title: string
  message: string
  action?: AssessmentModalBannerAction
}

export const getAssignmentStage = (assignment: Assignment) =>
  assignment.content_meta_data?.stage ?? assignment.content.stage ?? assignment.content.meta_data?.stage

export const FLEXIBLE_ASSESSMENT_STAGE = 'flexible'
export const ACTIVE_STATUS = 'ACTIVE'
export const PAUSED_STATUS = 'PAUSED'
export const ASSESSMENT_SEND_WINDOW_HOURS = 72

const PENDING_ASSIGNMENT_STATUSES = new Set(['new', 'opened', 'in_progress', 'draft'])

export const ASSESSMENT_MODAL_MESSAGES = defineMessages({
  sendOnceNow: {
    defaultMessage: 'Send once now',
    description: 'Frequency option for a one-time assessment sent immediately',
  },
  everySession: {
    defaultMessage: 'Every session',
    description: 'Frequency option for an assessment sent every session',
  },
  every2Sessions: {
    defaultMessage: 'Every 2 sessions',
    description: 'Frequency option for an assessment sent every two sessions',
  },
  every4Sessions: {
    defaultMessage: 'Every 4 sessions',
    description: 'Frequency option for an assessment sent every four sessions',
  },
  customRecurringFrequency: {
    defaultMessage: 'Every {frequency, plural, one {session} other {# sessions}}',
    description: 'Current custom frequency for an assessment being edited',
  },
  alreadyScheduledTitle: {
    defaultMessage: '{assessmentName} is already scheduled',
    description: 'Warning title when an assessment already has a recurring schedule',
  },
  recurringFrequency: {
    defaultMessage: 'every {frequency, plural, one {session} other {# sessions}}',
    description: 'Existing recurring assessment frequency in warning copy',
  },
  alreadyScheduled: {
    defaultMessage:
      'This client is already scheduled to receive a {assessmentName} {frequencyText}. This will override the existing schedule.',
    description: 'Information when replacing an existing recurring assessment schedule with another one',
  },
  alreadyScheduledAndReassign: {
    defaultMessage:
      "This client is already scheduled to receive a {assessmentName} {frequencyText}. This will override the existing schedule and you'll have to reassign it.",
    description: 'Warning when replacing an existing recurring assessment schedule with a one-time assessment',
  },
  outstandingTitle: {
    defaultMessage: '{assessmentName} has already been sent to this client',
    description: 'Warning title when a one-time assessment is still pending',
  },
  outstandingSendOnce: {
    defaultMessage:
      'The client has a pending {assessmentName} from {assessmentSendDate} they have not yet completed. Ask your client to complete the pending measure from their account.',
    description: 'Warning that a pending one-time assessment can only be finished by the client',
  },
  outstandingRecurring: {
    defaultMessage:
      'The client has a pending {assessmentName} from {assessmentSendDate} they have not yet completed. Editing the frequency of this measure will take effect for future sessions.',
    description:
      'Warning that putting a pending one-time assessment on a recurring schedule only affects future sessions',
  },
  clickHereToEdit: {
    defaultMessage: 'Click here to edit.',
    description: 'Link sending the provider to the edit flow for an assessment that already exists',
  },
  willSendImmediatelyTitle: {
    defaultMessage: 'Will send immediately',
    description: 'Title for a banner explaining a one-time assessment email',
  },
  willSendImmediately: {
    defaultMessage: 'The client will receive an email notification.',
    description: 'Information about a one-time assessment email',
  },
  sentBeforeSessionsTitle: {
    defaultMessage: 'Sent 72 hours before sessions',
    description: 'Information title for a recurring assessment scheduled more than 72 hours before the next session',
  },
  sentBeforeSessions: {
    defaultMessage: 'Sent 72 hours in advance, per the selected frequency.',
    description: 'Information about recurring assessment delivery timing',
  },
  startsAfterNextSessionTitle: {
    defaultMessage: 'Starts after your next session',
    description: 'Information title for a recurring assessment scheduled within 72 hours of the next session',
  },
  startsWithFollowingSession: {
    defaultMessage:
      'Because your upcoming session is less than 72 hours away, this recurring schedule will begin with the following session. If you need an assessment completed before your upcoming session, select Send once now instead.',
    description: 'Information about recurring assessment delivery when the next session is within 72 hours',
  },
})

const FREQUENCIES = [
  { value: ONE_OFF_FREQUENCY, message: ASSESSMENT_MODAL_MESSAGES.sendOnceNow },
  { value: 1, message: ASSESSMENT_MODAL_MESSAGES.everySession },
  { value: 2, message: ASSESSMENT_MODAL_MESSAGES.every2Sessions },
  { value: 4, message: ASSESSMENT_MODAL_MESSAGES.every4Sessions },
] as const

export const getFrequencyOptions = (formatMessage: IntlShape['formatMessage']): FrequencyOption[] =>
  FREQUENCIES.map(({ value, message }) => ({ label: formatMessage(message), value }))

export const getLatestFlexibleAssignment = (
  assignments: readonly Assignment[],
  episodeId: string,
  contentMetaDataId: string,
) =>
  assignments
    .filter(
      (assignment) =>
        assignment.episode_id === episodeId &&
        assignment.content_meta_data_id === contentMetaDataId &&
        getAssignmentStage(assignment) === AssessmentStages.flexible,
    )
    .reduce<Assignment | undefined>((latest, assignment) => {
      if (!latest) return assignment

      const assignmentTime = parseOutcomeResponseDate(assignment.create_date)?.getTime()
      const latestTime = parseOutcomeResponseDate(latest.create_date)?.getTime()
      if (assignmentTime === undefined) return latest
      return latestTime === undefined || assignmentTime > latestTime ? assignment : latest
    }, undefined)

export const isPendingAssignment = (assignment?: Assignment) =>
  Boolean(assignment && PENDING_ASSIGNMENT_STATUSES.has(assignment.status.toLowerCase()))

/** True when the next scheduled session is close enough that a new recurring schedule misses it. */
export const isNextSessionWithinSendWindow = (nextSessionDate?: Date, now: Date = new Date()) =>
  nextSessionDate !== undefined &&
  isWithinInterval(nextSessionDate, { start: now, end: addHours(now, ASSESSMENT_SEND_WINDOW_HOURS) })

export type AddAssessmentBannerArgs = {
  formatMessage: IntlShape['formatMessage']
  /** Display name of the assessment picked in the modal, or undefined while nothing is picked. */
  assessmentName?: string
  /** Frequency picked in the modal, or '' while nothing is picked. */
  frequency: AssessmentFrequency | ''
  /** Existing configuration for the picked assessment, if the client already has one. */
  existingAssessment?: Pick<FlexibleAssessment, 'frequency' | 'status'>
  /** Most recent assignment of the picked assessment, used to detect an unfinished one-off. */
  latestAssignment?: Assignment
  /** Start of the next scheduled session on the selected episode, if one is booked. */
  nextSessionDate?: Date
  now?: Date
}

/**
 * Resolves the single banner shown in the add assessment modal.
 *
 * A `warning` means the client already has this assessment and a second one cannot be added, so the
 * provider is sent to the edit flow instead; callers block submission while a warning is showing.
 * The existing configuration decides the warning on its own - the picked frequency is irrelevant to
 * it, and only shapes the informational copy once nothing is blocking.
 */
export const getAddAssessmentBanner = ({
  formatMessage,
  assessmentName,
  frequency,
  existingAssessment,
  latestAssignment,
  nextSessionDate,
  now,
}: AddAssessmentBannerArgs): AssessmentModalBanner | undefined => {
  if (assessmentName === undefined) return undefined

  if (existingAssessment?.status === ACTIVE_STATUS) {
    if (existingAssessment.frequency > ONE_OFF_FREQUENCY) {
      return {
        type: 'warning',
        title: formatMessage(ASSESSMENT_MODAL_MESSAGES.alreadyScheduledTitle, { assessmentName }),
        message: formatMessage(ASSESSMENT_MODAL_MESSAGES.clickHereToEdit),
        action: 'edit',
      }
    }

    if (existingAssessment.frequency === ONE_OFF_FREQUENCY && isPendingAssignment(latestAssignment)) {
      return {
        type: 'warning',
        title: formatMessage(ASSESSMENT_MODAL_MESSAGES.outstandingTitle, { assessmentName }),
        message: formatMessage(ASSESSMENT_MODAL_MESSAGES.clickHereToEdit),
        action: 'edit',
      }
    }
  }

  if (frequency === '') return undefined

  if (frequency === ONE_OFF_FREQUENCY) {
    return {
      type: 'info',
      title: formatMessage(ASSESSMENT_MODAL_MESSAGES.willSendImmediatelyTitle),
      message: formatMessage(ASSESSMENT_MODAL_MESSAGES.willSendImmediately),
    }
  }

  return isNextSessionWithinSendWindow(nextSessionDate, now)
    ? {
        type: 'info',
        title: formatMessage(ASSESSMENT_MODAL_MESSAGES.startsAfterNextSessionTitle),
        message: formatMessage(ASSESSMENT_MODAL_MESSAGES.startsWithFollowingSession),
      }
    : {
        type: 'info',
        title: formatMessage(ASSESSMENT_MODAL_MESSAGES.sentBeforeSessionsTitle),
        message: formatMessage(ASSESSMENT_MODAL_MESSAGES.sentBeforeSessions),
      }
}

export type EditAssessmentBannerArgs = {
  intl: Pick<IntlShape, 'formatDate' | 'formatMessage'>
  assessmentName: string
  /** Frequency picked in the modal, or '' while nothing is picked. */
  frequency: AssessmentFrequency | ''
  /** The configuration being edited. */
  existingAssessment: Pick<FlexibleAssessment, 'frequency' | 'status'>
  /** Most recent assignment of this assessment, used to detect an unfinished one-off. */
  latestAssignment?: Assignment
  /** Start of the next scheduled session on the selected episode, if one is booked. */
  nextSessionDate?: Date
  now?: Date
}

/**
 * Resolves the single banner shown in the edit assessment modal.
 *
 * Unlike the add flow, none of these banners block submission - the provider is already editing, so
 * they describe what the change replaces rather than preventing it. The existing configuration
 * decides the banner on its own, which is why one is shown as soon as the modal opens.
 */
export const getEditAssessmentBanner = ({
  intl,
  assessmentName,
  frequency,
  existingAssessment,
  latestAssignment,
  nextSessionDate,
  now,
}: EditAssessmentBannerArgs): AssessmentModalBanner | undefined => {
  if (frequency === '') return undefined

  const isSendOnce = frequency === ONE_OFF_FREQUENCY

  if (existingAssessment.status === ACTIVE_STATUS) {
    // An assignment the client never finished outranks everything else, whatever is being picked.
    if (existingAssessment.frequency === ONE_OFF_FREQUENCY && isPendingAssignment(latestAssignment)) {
      const parsedCreateDate = latestAssignment && parseOutcomeResponseDate(latestAssignment.create_date)
      const values = {
        assessmentName,
        assessmentSendDate: parsedCreateDate
          ? intl.formatDate(parsedCreateDate, { month: 'numeric', day: 'numeric', year: 'numeric' })
          : latestAssignment?.create_date,
      }

      return {
        type: 'warning',
        title: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.outstandingTitle, values),
        message: intl.formatMessage(
          isSendOnce ? ASSESSMENT_MODAL_MESSAGES.outstandingSendOnce : ASSESSMENT_MODAL_MESSAGES.outstandingRecurring,
          values,
        ),
      }
    }

    // An existing recurring schedule is described in terms of what replacing it costs, which takes
    // precedence over the next session timing below.
    if (existingAssessment.frequency > ONE_OFF_FREQUENCY) {
      const values = {
        assessmentName,
        frequencyText: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.recurringFrequency, {
          frequency: existingAssessment.frequency,
        }),
      }
      const title = intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.alreadyScheduledTitle, values)

      // Switching to a one-off drops the schedule and needs a manual reassignment, so it warns.
      return isSendOnce
        ? {
            type: 'warning',
            title,
            message: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.alreadyScheduledAndReassign, values),
          }
        : { type: 'info', title, message: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.alreadyScheduled, values) }
    }
  }

  if (isSendOnce) {
    return {
      type: 'info',
      title: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.willSendImmediatelyTitle),
      message: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.willSendImmediately),
    }
  }

  return isNextSessionWithinSendWindow(nextSessionDate, now)
    ? {
        type: 'info',
        title: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.startsAfterNextSessionTitle),
        message: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.startsWithFollowingSession),
      }
    : {
        type: 'info',
        title: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.sentBeforeSessionsTitle),
        message: intl.formatMessage(ASSESSMENT_MODAL_MESSAGES.sentBeforeSessions),
      }
}
